<!-- eslint-disable no-console -->
<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { NCard, NAlert, NButton, NSpace, NFlex, useMessage } from "naive-ui";
import { $t } from "@/locales";
import { useRouterPush } from "@/hooks/common/router";
import { useFormRules, useNaiveForm } from "@/hooks/common/form";
import { useCaptcha } from "@/hooks/business/captcha";
import { addPhone } from "@/service/api/auth";
import { useAppStore } from "@/store/modules/app";
import FallbackOtpExplanation from "./fallback-otp-explanation.vue";

const appStore = useAppStore();
interface Props {
    /** The login module */
    direction: string;
    message?: string;
    showBackButton?: boolean;
    compact?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
    message: undefined,
    showBackButton: true,
    compact: false,
});
defineOptions({
    name: "PhoneVerify",
});

const { toggleLoginModule } = useRouterPush();
const message = useMessage();
// formRef is used by template ref binding (suppress TS6133)
const { formRef, validate } = useNaiveForm();
void formRef; // Mark as intentionally used for template ref binding
const {
    label,
    isCounting,
    loading,
    getCaptcha,
    fallbackOtpProvider,
    captchaError,
    duplicatePhoneConflict,
} = useCaptcha();

// Fallback OTP explanation modal state
const showFallbackExplanation = ref(false);

// Submit button loading and inline feedback state
const submitLoading = ref(false);
const verificationError = ref("");
const verificationSuccess = ref(false);
const showDuplicatePhoneGuidance = ref(false);

interface FormModel {
    phone: string;
    code: string;
}

const model: FormModel = reactive({
    phone: "",
    code: "",
});

const rules = computed<Record<keyof FormModel, App.Global.FormRule[]>>(() => {
    const { formRules } = useFormRules();

    return {
        phone: formRules.phone,
        code: formRules.code,
    };
});

// Computed properties for fallback provider display
const hasFallbackProvider = computed(() => !!fallbackOtpProvider.value);

const fallbackProviderName = computed(() => {
    if (!fallbackOtpProvider.value) return "";
    return (
        $t(`page.login.codeLogin.fallbackProvider.${fallbackOtpProvider.value}`) ||
        fallbackOtpProvider.value
    );
});

const fallbackProviderLink = computed(() => {
    if (!fallbackOtpProvider.value) return "";
    return (
        $t(`page.login.codeLogin.fallbackProviderLink.${fallbackOtpProvider.value}`) ||
        ""
    );
});

function openFallbackProvider() {
    if (fallbackProviderLink.value) {
        window.open(fallbackProviderLink.value, "_blank");
    }
}

function clearVerificationFeedback() {
    verificationError.value = "";
    verificationSuccess.value = false;
    showDuplicatePhoneGuidance.value = false;
}

function getErrorMessage(statusCode?: number) {
    if (statusCode === 226) return $t("page.login.codeLogin.phoneAlreadyAssociated");
    if (statusCode === 429) return $t("billing.tooManyRequests");
    if (statusCode === 403) return $t("page.login.common.needLogin");
    if (statusCode === 400 || statusCode === 401) return $t("page.login.codeLogin.codeNotValid");
    return $t("page.login.codeLogin.sendFailed");
}

function showVerificationError(
    content: string,
    options: { duplicatePhone?: boolean; toast?: boolean } = {}
) {
    verificationSuccess.value = false;
    verificationError.value = content;
    showDuplicatePhoneGuidance.value = Boolean(options.duplicatePhone);

    if (options.toast === false) return;

    message.error(content, {
        duration: options.duplicatePhone ? 12000 : 7000,
        closable: true,
        keepAliveOnHover: true,
    });
}

function showVerificationSuccess(content: string) {
    verificationSuccess.value = true;
    verificationError.value = "";
    showDuplicatePhoneGuidance.value = false;
    message.success(content, { duration: 5000, closable: true, keepAliveOnHover: true });
}

async function handleGetCaptcha() {
    clearVerificationFeedback();

    const result = await getCaptcha(model.phone);
    if (result?.success === false && captchaError.value) {
        showVerificationError(captchaError.value, {
            duplicatePhone: duplicatePhoneConflict.value,
            toast: false,
        });
    }
}

async function handleSubmit() {
    clearVerificationFeedback();

    try {
        await validate();
    } catch (error) {
        showVerificationError($t("page.login.codeLogin.codeNotValid"));
        return;
    }

    submitLoading.value = true;

    try {
        const response = await addPhone(model.phone, model.code);
        if (!response) {
            showVerificationError($t("page.login.codeLogin.sendFailed"));
            return;
        }

        const statusCode = response.statusCode ?? response.error?.response?.status;

        if (statusCode === 226 || statusCode === 429 || statusCode === 403) {
            showVerificationError(getErrorMessage(statusCode), { duplicatePhone: statusCode === 226 });
            return;
        }

        if (!response.error && response.data) {
            showVerificationSuccess($t("common.phoneAlreadyVerified"));
            setTimeout(() => {
                appStore.reloadPage();
            }, 1200);
            return;
        }

        showVerificationError(getErrorMessage(statusCode), { duplicatePhone: statusCode === 226 });
    } catch (error: any) {
        const statusCode = error?.response?.status;
        showVerificationError(getErrorMessage(statusCode), { duplicatePhone: statusCode === 226 });
    } finally {
        submitLoading.value = false;
    }
}
</script>

<template>
    <div :class="['phone-verify', { 'phone-verify--compact': props.compact }]">
        <NSpace vertical :size="props.compact ? 12 : 16" :style="{ direction: props.direction }">
            <NCard :bordered="false" size="small" class="card-wrapper phone-verify-outer-card">
                <NSpace vertical :size="props.compact ? 0 : 8" justify="center" class="mx-auto max-w-lg phone-verify-inner-space">
                    <NCard bordered size="small" class="card-wrapper phone-verify-inner-card">
                        <NAlert type="info" show-icon class="phone-verify-alert">
                            {{ props.message || $t("common.phoneVerificationRequired") }}
                        </NAlert>
                        <div v-if="!props.compact" class="phone-verify-spacer" />
                        <NForm ref="formRef" :model="model" :rules="rules" :size="props.compact ? 'medium' : 'large'" :show-label="false" class="phone-verify-form">
                            <NFormItem path="phone">
                                <NInput v-model:value="model.phone" class="phone-verify-input"
                                    :placeholder="$t('page.login.common.phonePlaceholder')" @input="clearVerificationFeedback" />
                            </NFormItem>
                            <NFormItem path="code">
                                <div class="w-full flex-y-center gap-16px phone-verify-code-row">
                                    <NInput v-model:value="model.code" class="phone-verify-input"
                                        :placeholder="$t('page.login.common.codePlaceholder')" @input="clearVerificationFeedback" />
                                    <NButton class="phone-verify-code-button" :size="props.compact ? 'medium' : 'large'" :disabled="isCounting" :loading="loading"
                                        @click="handleGetCaptcha">
                                        {{ label }}
                                    </NButton>
                                </div>
                            </NFormItem>

                            <Transition name="phone-verify-feedback">
                                <NAlert v-if="verificationError" type="error" show-icon class="phone-verify-feedback">
                                    <div class="phone-verify-feedback-content">
                                        <strong>{{ verificationError }}</strong>
                                        <p v-if="showDuplicatePhoneGuidance" class="phone-verify-feedback-help">
                                            {{ $t("page.login.codeLogin.phoneAlreadyAssociatedHelp") }}
                                        </p>
                                        <a v-if="showDuplicatePhoneGuidance" href="/account/delete" target="_blank" rel="noopener" class="phone-verify-delete-link">
                                            <NButton type="error" size="small" secondary>
                                                <NFlex align="center" :size="6">
                                                    {{ $t("page.login.codeLogin.deleteLinkedAccount") }}
                                                    <span aria-hidden="true">↗</span>
                                                </NFlex>
                                            </NButton>
                                        </a>
                                    </div>
                                </NAlert>
                                <NAlert v-else-if="verificationSuccess" type="success" show-icon class="phone-verify-feedback">
                                    {{ $t("common.phoneAlreadyVerified") }}
                                </NAlert>
                            </Transition>

                            <!-- Fallback OTP Provider Alert -->
                            <NAlert v-if="hasFallbackProvider && isCounting" type="warning" show-icon
                                class="fallback-alert">
                                <template #header>
                                    {{
                                        $t("page.login.codeLogin.sendCodeSuccessViaFallback", {
                                            provider: fallbackProviderName,
                                    })
                                    }}
                                </template>
                                <p class="fallback-description">
                                    {{
                                        $t("page.login.codeLogin.fallbackOtpSmsNotResponding", {
                                            provider: fallbackProviderName,
                                    })
                                    }}
                                </p>
                                <NSpace class="fallback-actions">
                                    <NButton text type="primary" @click="openFallbackProvider">
                                        {{
                                            $t("page.login.codeLogin.openFallbackProvider", {
                                                provider: fallbackProviderName,
                                        })
                                        }}
                                    </NButton>
                                    <NButton text @click="showFallbackExplanation = true">
                                        {{ $t("page.login.codeLogin.fallbackOtpInfoButton") }}
                                    </NButton>
                                </NSpace>
                            </NAlert>

                            <NSpace vertical :size="18" class="w-full">
                                <NButton type="primary" :size="props.compact ? 'medium' : 'large'" round block :loading="submitLoading" @click="handleSubmit">
                                    {{ $t("common.confirm") }}
                                </NButton>
                                <NButton v-if="props.showBackButton" :size="props.compact ? 'medium' : 'large'" round block @click="toggleLoginModule('pwd-login')">
                                    {{ $t("page.login.common.back") }}
                                </NButton>
                            </NSpace>
                        </NForm>
                    </NCard>
                </NSpace>
            </NCard>
        </NSpace>

        <!-- Fallback OTP Explanation Modal -->
        <FallbackOtpExplanation v-model:visible="showFallbackExplanation" :direction="props.direction"
            :fallback-provider="fallbackOtpProvider" />
    </div>
</template>

<style scoped>
.phone-verify {
    width: 100%;
}

.phone-verify :deep(.phone-verify-inner-space) {
    max-width: 28rem;
    width: 100%;
}

.phone-verify-form {
    margin: 0 auto;
    max-width: 24rem;
    width: 100%;
}

.phone-verify-code-button {
    min-width: 7.5rem;
    white-space: nowrap;
}

.phone-verify-feedback {
    margin-bottom: 14px;
    overflow: hidden;
}

.phone-verify-feedback-enter-active,
.phone-verify-feedback-leave-active {
    max-height: 12rem;
    opacity: 1;
    transform: translateY(0);
    transition:
        max-height 0.28s ease,
        opacity 0.2s ease,
        transform 0.2s ease,
        margin-bottom 0.28s ease;
}

.phone-verify-feedback-enter-from,
.phone-verify-feedback-leave-to {
    max-height: 0;
    margin-bottom: 0;
    opacity: 0;
    transform: translateY(-6px);
}

.phone-verify-feedback-content {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.phone-verify-feedback-help {
    margin: 0;
    font-size: 12px;
    line-height: 1.6;
}

.phone-verify-delete-link {
    align-self: flex-start;
    text-decoration: none;
}

.phone-verify--compact {
    width: 100%;
}

.phone-verify--compact :deep(.phone-verify-outer-card),
.phone-verify--compact :deep(.phone-verify-inner-card) {
    background: transparent;
    border: 0;
    box-shadow: none;
}

.phone-verify--compact :deep(.phone-verify-outer-card > .n-card__content),
.phone-verify--compact :deep(.phone-verify-inner-card > .n-card__content) {
    padding: 0 !important;
}

.phone-verify--compact :deep(.phone-verify-inner-space),
.phone-verify--compact .phone-verify-form {
    max-width: 100%;
}

.phone-verify-spacer {
    height: 1rem;
}

.phone-verify--compact .phone-verify-alert {
    margin-bottom: 12px;
}

@media (max-width: 640px) {
    .phone-verify :deep(.phone-verify-inner-space),
    .phone-verify-form {
        max-width: 100%;
    }

    .phone-verify-code-row {
        align-items: stretch;
        flex-direction: column;
        gap: 10px;
    }

    .phone-verify-code-row :deep(.n-button) {
        width: 100%;
    }

    .phone-verify :deep(.n-form-item) {
        margin-bottom: 12px;
    }
}

.fallback-alert {
    margin-bottom: 16px;
}

.fallback-description {
    font-size: 13px;
    margin-bottom: 8px;
    line-height: 1.5;
}

.fallback-actions {
    margin-top: 8px;
}
</style>
